import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { FieldWrapper } from '@progress/kendo-react-form';
import { Loader } from "@progress/kendo-react-indicators";
import { Input, MaskedTextBox } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { formatNumber } from '@progress/kendo-intl';
import dayjs from 'dayjs';
import TimeAgo from 'react-timeago';
import { cloneElement, useEffect, useRef, useState } from 'react';
import CenterDivPanel from "../../components/CenterDivPanel";
import { fetchApi } from '../../services/api';
import { JsonResponse } from "TypeGen/json-response";
import { ViewModel } from "TypeGen/Order/Planning/CarrierHighwayVehicles/view-model";
import { HighwayVehicleViewModel } from "TypeGen/Order/Planning/CarrierHighwayVehicles/highway-vehicle-view-model";
import { ComboBox, ComboBoxHandle, ListItemProps } from "@progress/kendo-react-dropdowns";

type Props = {
    tripId: number;
    onClose: () => void;
}

const CarrierTripParameters = (props: Props) => {
    const [loading, setLoading] = useState(true);
    const [highwayVehicles, setHighwayVehicles] = useState<HighwayVehicleViewModel[]>([]);
    const [highwayVehiclesLoaded, setHighwayVehiclesLoaded] = useState(false);

    const [tripNumber, setTripNumber] = useState<number>(null);
    const [vehicleNumber, setVehicleNumber] = useState<HighwayVehicleViewModel>(null);
    const [trailerNumber, setTrailerNumber] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const _save = () => {
        setLoading(true);

        const data = {
            VehicleNumber: vehicleNumber?.TruckNumber || '',
            TrailerNumber: trailerNumber,
            TrackingNumber: trackingNumber,
            PhoneNumber: phoneNumber
        }

        fetchApi(`/api/Track/CarrierTripParameters/${props.tripId}`, data, 'POST')
            .then((response: JsonResponse) => {
                if (response.Success) {
                    props.onClose();
                } else {
                    alert(response.ErrorMessage);
                }
            }
            ).catch((e) => {
                // If not problem details
                if (!e?.status) alert(e);
            }).finally(() => setLoading(false));
    }

    const fetchHighwayVehicles = () => {
        fetchApi(`/api/Track/CarrierHighwayVehicles/${props.tripId}`)
            .then((response: ViewModel) => {
                setHighwayVehicles(response.Vehicles);
                setHighwayVehiclesLoaded(true);
            })
            .catch(err => {
                // No need to alert, just log
                console.error('Error fetching carrier highway vehicles:', err);
            });
    };

    useEffect(() => {
        setLoading(true);
        fetchApi(`/api/Track/CarrierTripParameters/${props.tripId}`)
            .then((response: { VehicleNumber: string, TrailerNumber: string, TrackingNumber: string, PhoneNumber: string, TripNumber: number }) => {
                setVehicleNumber({ TruckNumber: response.VehicleNumber } as HighwayVehicleViewModel);
                setTrailerNumber(response.TrailerNumber);
                setTrackingNumber(response.TrackingNumber);
                setPhoneNumber(response.PhoneNumber);
                setTripNumber(response.TripNumber);
                setLoading(false);
            });
    }, [props.tripId]);

    const renderHighwayVehicle = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        const itemChildren = <div className='w-100'>
            <span>{itemProps.dataItem.TruckNumber} ({formatNumber(itemProps.dataItem.DistanceFromOrigin, "n0")} miles out)</span><br />
            <span className='k-text-subtle'>{itemProps.dataItem.AreaDescription} - {itemProps.dataItem.LocatedAt ? <TimeAgo date={dayjs.utc(itemProps.dataItem.LocatedAt).toDate()} /> : ''}</span>
        </div>
        return cloneElement(li, li.props, itemChildren);
    }

    return <>
        <Dialog
            className="dialog-w800"
            title={`Carrier Trip Parameters ${tripNumber ?? ''}`}
            onClose={() => {
                props.onClose();
            }}
        >
            {loading && <CenterDivPanel>
                <Loader type="converging-spinner" />
            </CenterDivPanel>}

            <div className="k-form k-form-md p-0">
                <FieldWrapper>
                    <Label editorId="vehicleNumber">Vehicle Number</Label>
                    <ComboBox ignoreCase={true}
                        allowCustom
                        clearButton={!!vehicleNumber}
                        disabled={loading}
                        id="vehicleNumber"
                        data={highwayVehicles}
                        onOpen={() => {
                            if (!highwayVehiclesLoaded) {
                                fetchHighwayVehicles();
                            }
                        }}
                        textField="TruckNumber"
                        itemRender={renderHighwayVehicle}
                        inputAttributes={{ maxLength: 15 }}
                        onChange={x => setVehicleNumber(x.value ?? '')}
                        value={vehicleNumber ?? ''}
                        footer={<span className="k-text-subtle ml-2">Powered by Highway</span>}
                        placeholder="Vehicle Number"
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label editorId="trailerNumber">Trailer Number</Label>
                    <Input
                        maxLength={15}
                        id="trailerNumber"
                        disabled={loading}
                        value={trailerNumber ?? ''}
                        onChange={(e) => setTrailerNumber(e.value ?? '')}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label editorId="trackingNumber">Tracking Number</Label>
                    <Input
                        maxLength={30}
                        id="trackingNumber"
                        disabled={loading}
                        value={trackingNumber ?? ''}
                        onChange={(e) => setTrackingNumber(e.value ?? '')}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label>Phone Number</Label>
                    <FieldWrapper>
                        <MaskedTextBox
                            mask="(000) 000-0000"
                            value={phoneNumber ?? ''}
                            disabled={loading}
                            onChange={(e) => setPhoneNumber(e.value ?? '')}
                        />
                    </FieldWrapper>
                </FieldWrapper>
            </div>

            <DialogActionsBar layout={"end"}>
                <Button onClick={props.onClose}>Cancel</Button>
                <Button themeColor="primary" onClick={_save}>
                    Save
                </Button>
            </DialogActionsBar>
        </Dialog>
    </>

}
export default CarrierTripParameters;